import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../../i18n';
import { CATCH_SHOUT_CATCH_CONFIG } from './constants';

export interface CatchShoutCatchGathererProps {
  animals: string[];
  timeLimit: number;
  startTime: number;
  whispererName?: string;
  disabled?: boolean;
  onCatch: (animalEmoji: string, spawnId: string) => void;
}

interface FallingAnimal {
  id: number;
  emoji: string;
  xPercent: number;
  yPercent: number;
  speedMultiplier: number;
}

const FALL_SPEED = CATCH_SHOUT_CATCH_CONFIG.fallSpeedPerSecond;
const FALL_SPEED_MUL_MIN = CATCH_SHOUT_CATCH_CONFIG.fallSpeedMultiplierMin;
const FALL_SPEED_MUL_MAX = CATCH_SHOUT_CATCH_CONFIG.fallSpeedMultiplierMax;
const SPAWN_INTERVAL = CATCH_SHOUT_CATCH_CONFIG.spawnIntervalMs;
const SPAWN_COUNT_MAX = CATCH_SHOUT_CATCH_CONFIG.spawnCountMax;
const PLATFORM_WIDTH = CATCH_SHOUT_CATCH_CONFIG.platformWidthFraction;
const CATCH_MARGIN = CATCH_SHOUT_CATCH_CONFIG.catchMarginFraction;
const PLATFORM_STRIP_HEIGHT_PX = CATCH_SHOUT_CATCH_CONFIG.platformStripHeightPx;
const FENCE_WIDTH_PX = CATCH_SHOUT_CATCH_CONFIG.fenceWidthPx;
const PLATFORM_INSET_PX = 8;

function pickAnimal(animals: string[]): string {
  return animals[Math.floor(Math.random() * animals.length)] ?? '🐱';
}

function randomSpeedMultiplier(): number {
  return FALL_SPEED_MUL_MIN + Math.random() * (FALL_SPEED_MUL_MAX - FALL_SPEED_MUL_MIN);
}

export const CatchShoutCatchGatherer: React.FC<CatchShoutCatchGathererProps> = ({
  animals,
  timeLimit,
  startTime,
  whispererName,
  disabled = false,
  onCatch,
}) => {
  const { t } = useTranslation();
  const [falling, setFalling] = useState<FallingAnimal[]>([]);
  const [platformLeft, setPlatformLeft] = useState(0.5 - PLATFORM_WIDTH / 2);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef(0);
  const dragStartLeft = useRef(0);
  const nextId = useRef(0);
  const lastSpawn = useRef(0);
  const lastTick = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const onCatchRef = useRef(onCatch);
  const platformLeftRef = useRef(platformLeft);
  const caughtIds = useRef(new Set<number>());

  platformLeftRef.current = platformLeft;

  useEffect(() => {
    onCatchRef.current = onCatch;
  }, [onCatch]);

  useEffect(() => {
    if (disabled) {
      setFalling([]);
      caughtIds.current.clear();
    }
  }, [disabled]);

  const gameActive = !disabled && Date.now() - startTime < timeLimit;

  useEffect(() => {
    if (!gameActive || !animals.length) return;
    lastSpawn.current = Date.now();
    lastTick.current = Date.now();

    const interval = setInterval(() => {
      const now = Date.now();
      if (now - lastSpawn.current < SPAWN_INTERVAL) return;
      lastSpawn.current = now;
      const count = 1 + Math.floor(Math.random() * SPAWN_COUNT_MAX);
      const newOnes: FallingAnimal[] = [];
      for (let i = 0; i < count; i++) {
        newOnes.push({
          id: nextId.current++,
          emoji: pickAnimal(animals),
          xPercent: 10 + Math.random() * 80,
          yPercent: 0,
          speedMultiplier: randomSpeedMultiplier(),
        });
      }
      setFalling((prev) => [...prev, ...newOnes]);
    }, 250);
    return () => clearInterval(interval);
  }, [gameActive, animals]);

  useEffect(() => {
    if (!gameActive) return;
    let raf = 0;
    const tick = () => {
      const now = Date.now();
      const dt = Math.min((now - lastTick.current) / 1000, 0.1);
      lastTick.current = now;
      const pl = platformLeftRef.current;
      setFalling((prev) => {
        const next: FallingAnimal[] = [];
        for (const a of prev) {
          const newY = a.yPercent + FALL_SPEED * a.speedMultiplier * dt * 100;
          if (newY >= 100) {
            const platformLeftPercent = pl * 100;
            const platformRightPercent = (pl + PLATFORM_WIDTH) * 100;
            const margin = CATCH_MARGIN * 100;
            if (
              a.xPercent >= platformLeftPercent - margin &&
              a.xPercent <= platformRightPercent + margin &&
              !caughtIds.current.has(a.id)
            ) {
              caughtIds.current.add(a.id);
              onCatchRef.current(a.emoji, String(a.id));
            }
            continue;
          }
          next.push({ ...a, yPercent: newY });
        }
        return next;
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [gameActive]);

  const handlePointerDown = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (disabled || !gameActive) return;
      const clientX = 'touches' in e ? e.touches[0]?.clientX : e.clientX;
      if (clientX == null) return;
      setIsDragging(true);
      dragStartX.current = clientX;
      dragStartLeft.current = platformLeft;
    },
    [disabled, gameActive, platformLeft]
  );

  const handlePointerMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!isDragging) return;
      const clientX = 'touches' in e ? (e as TouchEvent).touches[0]?.clientX : (e as MouseEvent).clientX;
      const container = containerRef.current;
      if (!container || clientX == null) return;
      const rect = container.getBoundingClientRect();
      const deltaX = (clientX - dragStartX.current) / rect.width;
      let next = dragStartLeft.current + deltaX;
      next = Math.max(0, Math.min(1 - PLATFORM_WIDTH, next));
      setPlatformLeft(next);
    },
    [isDragging]
  );

  const handlePointerUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      dragStartX.current = 0;
      dragStartLeft.current = platformLeft;
    }
  }, [isDragging, platformLeft]);

  useEffect(() => {
    window.addEventListener('mousemove', handlePointerMove);
    window.addEventListener('mouseup', handlePointerUp);
    window.addEventListener('touchmove', handlePointerMove, { passive: true });
    window.addEventListener('touchend', handlePointerUp);
    return () => {
      window.removeEventListener('mousemove', handlePointerMove);
      window.removeEventListener('mouseup', handlePointerUp);
      window.removeEventListener('touchmove', handlePointerMove);
      window.removeEventListener('touchend', handlePointerUp);
    };
  }, [handlePointerMove, handlePointerUp]);

  const platformHeightPx = CATCH_SHOUT_CATCH_CONFIG.platformHeightPx;
  const platformStyle: React.CSSProperties = {
    position: 'absolute',
    bottom: PLATFORM_INSET_PX,
    left: `${platformLeft * 100}%`,
    width: `${PLATFORM_WIDTH * 100}%`,
    height: platformHeightPx,
    maxWidth: '100%',
    boxSizing: 'border-box',
    backgroundColor: 'rgba(52, 211, 153, 0.9)',
    borderRadius: 14,
    border: '3px solid rgba(52, 211, 153, 1)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    cursor: disabled ? 'default' : 'grab',
    userSelect: 'none',
    transition: isDragging ? 'none' : 'left 0.05s ease-out',
    pointerEvents: disabled ? 'none' : 'auto',
    zIndex: 10,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
  };

  const fenceHeightPx = 20;
  const fenceStyle: React.CSSProperties = {
    width: FENCE_WIDTH_PX,
    height: fenceHeightPx,
    backgroundColor: 'rgba(52, 211, 153, 1)',
    border: '2px solid rgba(34, 160, 115, 1)',
    borderRadius: 2,
    flexShrink: 0,
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 0',
        minHeight: 0,
        height: '100%',
        overflow: 'hidden',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          flexShrink: 0,
          fontSize: '0.95rem',
          fontWeight: 600,
          color: 'var(--text-secondary)',
          padding: '0.35rem 1rem 0.5rem',
        }}
      >
        {whispererName
          ? t('gameplay.catchShoutCatch.listenToGo', { name: whispererName })
          : t('gameplay.catchShoutCatch.listenToTeammateGo')}
      </div>

      <div
        ref={containerRef}
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          overflow: 'hidden',
          opacity: disabled ? 0.6 : 1,
          transition: 'opacity 0.3s',
          touchAction: 'none',
        }}
      >
        <div
          style={{
            flex: '1 1 auto',
            minHeight: 0,
            position: 'relative',
            width: '100%',
            overflow: 'hidden',
          }}
        >
          {falling.map((a) => (
            <div
              key={a.id}
              style={{
                position: 'absolute',
                left: `${a.xPercent}%`,
                top: `${a.yPercent}%`,
                transform: 'translate(-50%, -50%)',
                fontSize: CATCH_SHOUT_CATCH_CONFIG.emojiSizePx,
                lineHeight: 1,
                pointerEvents: 'none',
                zIndex: 5,
              }}
            >
              {a.emoji}
            </div>
          ))}
        </div>

        <div
          style={{
            flexShrink: 0,
            height: PLATFORM_STRIP_HEIGHT_PX,
            position: 'relative',
            width: '100%',
          }}
        >
          <div
            role="slider"
            aria-label={t('bonus.player.catchPlatformAria')}
            style={platformStyle}
            onMouseDown={handlePointerDown}
            onTouchStart={handlePointerDown}
          >
            <div style={fenceStyle} />
            <div style={{ ...fenceStyle, order: 1 }} />
          </div>
        </div>
      </div>
    </div>
  );
};
